import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Edit2, Trash2, EyeOff, FileText, Search, Settings } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { Note, NoteAuthorKind, NoteType } from '@terra/api-client';
import { Badge, Button, Card, CardBody, EmptyState, Input, PageHeader, Spinner } from '@terra/ui';

const NOTE_TYPES: NoteType[] = ['REFUND', 'WARRANTY', 'SHIPPING', 'RETURN', 'PAYMENT', 'GENERAL'];
const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();
const authorLabel = (k: NoteAuthorKind) => (k === 'IN_HOUSE' ? 'In-House' : 'Seller');

// Lazy import of the singleton API client.
import { api } from '../lib/api';

// ── Small toggle switch ──────────────────────────────────────────────────────

function Toggle({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 disabled:opacity-50 ${
        on ? 'bg-gold-400 border-gold-400' : 'bg-surface-subtle border-border/60 border'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
          on ? 'translate-x-[20px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
// ADD / EDIT NOTE  — "Note Information"
// ══════════════════════════════════════════════════════════════════

export function AddNotePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('id');

  // For edit, prefill from the list (no dedicated single-note endpoint needed).
  const existing = useQuery({
    queryKey: ['notes-all-for-edit'],
    queryFn: () => api.notes.list(),
    enabled: !!editId,
  });
  const editTarget = editId ? existing.data?.find((n) => n.id === editId) : undefined;

  const [type, setType] = useState<NoteType>('REFUND');
  const [description, setDescription] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the form once when editing.
  if (editId && editTarget && !hydrated) {
    setType(editTarget.type);
    setDescription(editTarget.description);
    setHydrated(true);
  }

  const save = useMutation({
    mutationFn: () =>
      editId
        ? api.notes.update(editId, { type, description })
        : api.notes.create({ type, description }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notes'] });
      navigate('/notes');
    },
  });

  return (
    <div className="pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="border-border/60 bg-surface max-w-3xl overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <FileText className="h-4 w-4" strokeWidth={2} />
          </div>
          <h3 className="text-ink text-[14px] font-bold">Note Information</h3>
        </div>
        <div className="p-5">
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (description.trim().length > 0) save.mutate();
            }}
          >
            <div className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
              <label className="text-ink text-[13px] font-semibold">Type</label>
              <select
                className="border-border/60 bg-surface-subtle/50 text-ink focus:border-ink focus:bg-surface focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                value={type}
                onChange={(e) => setType(e.target.value as NoteType)}
              >
                {NOTE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {titleCase(t)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
              <div>
                <label className="text-ink text-[13px] font-semibold">Description</label>
                <p className="text-ink-muted mt-1 text-[11px] font-medium">(Max 900 character)</p>
              </div>
              <textarea
                className="border-border/60 bg-surface-subtle/50 text-ink placeholder:text-ink-subtle focus:border-ink focus:bg-surface focus:ring-ink/20 h-44 w-full rounded-xl border p-3.5 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                maxLength={900}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Write your note here..."
              />
            </div>

            <div className="border-border/40 flex justify-end border-t pt-2">
              <button
                type="submit"
                disabled={save.isPending || !description.trim()}
                className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-6 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm sm:w-auto"
              >
                {save.isPending ? 'Saving...' : 'Save Note'}
              </button>
            </div>
            {save.isError && (
              <p className="text-right text-[12px] font-semibold text-red-500">
                Could not save the note.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NOTE LIST  — "All Notes"
// ══════════════════════════════════════════════════════════════════

type Tab = 'ALL' | 'IN_HOUSE' | 'SELLER';

export function NoteListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('ALL');
  const [search, setSearch] = useState('');
  const [viewId, setViewId] = useState<string | null>(null);

  const config = useQuery({ queryKey: ['notes-config'], queryFn: () => api.notes.getConfig() });
  const notes = useQuery({
    queryKey: ['notes', tab, search],
    queryFn: () =>
      api.notes.list({
        ...(tab !== 'ALL' ? { authorKind: tab } : {}),
        ...(search ? { q: search } : {}),
      }),
  });

  const setConfig = useMutation({
    mutationFn: (v: boolean) => api.notes.updateConfig(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes-config'] }),
  });
  const toggleAccess = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.notes.update(id, { sellerCanAccess: value }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.notes.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });

  const rows = notes.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      {/* Topbar renders the title, no local header needed here */}
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Global config */}
        <div className="border-border/60 bg-surface flex flex-1 items-center justify-between gap-4 overflow-hidden rounded-2xl border px-4 py-3 shadow-sm sm:max-w-md">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Settings className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[13px] font-bold">Allow sellers to add notes?</span>
          </div>
          <Toggle
            on={config.data?.sellerCanAddNote ?? false}
            disabled={config.isLoading || setConfig.isPending}
            onClick={() => setConfig.mutate(!(config.data?.sellerCanAddNote ?? false))}
          />
        </div>

        <button
          onClick={() => navigate('/notes/new')}
          className="bg-ink hover:bg-ink/90 flex h-10 shrink-0 items-center justify-center rounded-xl px-5 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          Add New Note
        </button>
      </div>

      {/* List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col items-center justify-between gap-4 border-b p-4 sm:flex-row">
          <div className="flex gap-2">
            {(['ALL', 'IN_HOUSE', 'SELLER'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`rounded-xl px-4 py-1.5 text-[12px] font-bold transition-colors duration-200 ${
                  tab === t
                    ? 'bg-ink text-white'
                    : 'bg-surface-subtle text-ink-muted hover:bg-surface-subtle/80 hover:text-ink'
                }`}
              >
                {t === 'ALL' ? 'All' : t === 'IN_HOUSE' ? 'In-House' : 'Seller'}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
            </div>
            <input
              type="text"
              placeholder="Search notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-border/80 bg-surface text-ink focus:border-ink focus:ring-ink/20 placeholder:text-ink-subtle h-10 w-full rounded-xl border pl-9 pr-3 text-[13px] font-medium shadow-sm outline-none transition-colors focus:ring-2"
            />
          </div>
        </div>

        <div>
          {notes.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner size="md" />
              <span className="text-ink-muted text-[13px] font-medium">Loading notes...</span>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title="No notes" description="Notes you add will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-border/60 bg-surface-subtle/30 text-ink-muted border-b text-[11px] font-bold uppercase tracking-wider">
                    <th className="px-5 py-3.5">#</th>
                    <th className="px-5 py-3.5">User</th>
                    <th className="px-5 py-3.5">Type</th>
                    <th className="px-5 py-3.5">Description</th>
                    <th className="px-5 py-3.5 text-center">Seller Access</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((note: Note, idx: number) => (
                    <RowGroup
                      key={note.id}
                      note={note}
                      index={idx + 1}
                      expanded={viewId === note.id}
                      onView={() => setViewId(viewId === note.id ? null : note.id)}
                      onToggleAccess={(v) => toggleAccess.mutate({ id: note.id, value: v })}
                      onDelete={() => {
                        if (window.confirm('Delete this note?')) remove.mutate(note.id);
                      }}
                      toggleBusy={toggleAccess.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Table row + expandable detail ────────────────────────────────────────────

function RowGroup({
  note,
  index,
  expanded,
  onView,
  onToggleAccess,
  onDelete,
  toggleBusy,
}: {
  note: Note;
  index: number;
  expanded: boolean;
  onView: () => void;
  onToggleAccess: (v: boolean) => void;
  onDelete: () => void;
  toggleBusy: boolean;
}) {
  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: (index % 15) * 0.02, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={`border-border/60 hover:bg-surface-subtle/30 border-b transition-colors ${expanded ? 'bg-surface-subtle/50' : ''}`}
      >
        <td className="text-ink-muted px-5 py-4 text-[13px] font-bold">{index}</td>
        <td className="px-5 py-4">
          <Badge
            tone={note.authorKind === 'IN_HOUSE' ? 'neutral' : 'gold'}
            className="px-1.5 py-0.5 text-[10px] font-bold"
          >
            {authorLabel(note.authorKind)}
          </Badge>
        </td>
        <td className="text-ink px-5 py-4 text-[13px] font-bold">{titleCase(note.type)}</td>
        <td className="text-ink-muted max-w-xs px-5 py-4 text-[13px] font-medium">
          <span className="line-clamp-1">{note.description}</span>
        </td>
        <td className="px-5 py-4">
          <div className="flex justify-center">
            <Toggle
              on={note.sellerCanAccess}
              disabled={toggleBusy}
              onClick={() => onToggleAccess(!note.sellerCanAccess)}
            />
          </div>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={onView}
              className="text-ink-subtle hover:text-ink hover:bg-surface-subtle rounded p-1.5 transition-colors"
              title="View"
            >
              <Eye className="h-4 w-4" strokeWidth={2} />
            </button>
            <Link
              to={`/notes/new?id=${note.id}`}
              className="text-ink-subtle hover:text-ink hover:bg-surface-subtle rounded p-1.5 transition-colors"
              title="Edit"
            >
              <Edit2 className="h-4 w-4" strokeWidth={2} />
            </Link>
            <button
              type="button"
              onClick={onDelete}
              className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </td>
      </motion.tr>
      <AnimatePresence>
        {expanded && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-surface-subtle/50"
          >
            <td colSpan={6} className="border-border/60 border-b px-5 py-5">
              <div className="flex max-w-3xl flex-col gap-3">
                <div>
                  <h4 className="text-ink-muted mb-1 text-[11px] font-bold uppercase tracking-wider">
                    Full Description
                  </h4>
                  <div className="border-border/60 bg-surface text-ink whitespace-pre-wrap rounded-xl border p-4 text-[13px] font-medium leading-relaxed shadow-sm">
                    {note.description}
                  </div>
                </div>
                <div className="text-ink-muted flex items-center gap-4 text-[11px] font-medium">
                  <span>Created: {new Date(note.createdAt).toLocaleString()}</span>
                  <span>•</span>
                  <span>Updated: {new Date(note.updatedAt).toLocaleString()}</span>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}
